import { settingsWords as words, settingsText, settingsCopy as copy } from "./settings-copy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Box } from "@/components/ui/box.schema";
import {
  zoomRecordingReceiverApi,
  type ZoomRecordingReceiverStatus,
} from "@/lib/api/settings";
import {
  boxRead,
  imam,
  resultState,
  useSettingsCommit,
} from "./settings-boxes";

export function useZoomReceiverBoxes(enabled: boolean): Box[] {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "zoom-recording-receiver"],
    queryFn: zoomRecordingReceiverApi.get,
    enabled,
    retry: false,
  });
  const outcome = useSettingsCommit();
  const read = boxRead(query, copy.eventUnavailable);
  const value = query.data;
  const receiver = value?.receiver;
  const current = Boolean(
    receiver && receiver.user_id === value?.current_actor_user_id,
  );
  const ready = Boolean(value?.ready);
  const label = ready
    ? current
      ? words.thisAdminReceivesEvents
      : words.anotherAdminReceivesEvents
    : receiver
      ? words.receiverNeedsReplacement
      : words.receiverNotBound;
  return [
    {
      id: "zoom-event-processing",
      job: words.readZoomEventProcessingCapability,
      kind: "fact",
      label: words.zoomEventProcessing,
      owner: {
        system: "zoom",
        readAt: read.status === "ready" || read.status === "stale" ? read.readAt : null,
      },
      fold: "never",
      read,
      prose: (raw) => {
        const value = raw as ZoomRecordingReceiverStatus;
        return settingsText.eventProcessing(
          value.provider_configured,
          value.ingestion_enabled,
        );
      },
      detail: () =>
        words.zoomOwnsRecordingEventsThisCapabilityNeverChangesCourseDeliveryPolicy,
    },
    {
      id: "zoom-recording-receiver",
      job: words.bindTheCurrentAdministratorAsTheRecordingEventReceiver,
      kind: "task",
      label: words.zoomRecordingReceiver,
      owner: imam,
      read: boxRead(query, copy.receiverUnavailable),
      fold: "settled",
      draft: false,
      result: outcome.result,
      state: () =>
        resultState(outcome.result, {
          class: ready ? "done" : "waiting",
          word: label,
        }),
      detail: () =>
        receiver
          ? settingsText.verified(receiver.verified_at)
          : words.bindTheSignedInIMAMAdministratorToAuthorizeRecordingEventProcessing,
      act: {
        verb: receiver ? words.replace : words.bindThisAdministrator,
        does: "open",
      },
      exits:
        receiver && !current && ready
          ? [{ label: words.replace, reopens: true }]
          : undefined,
      candidate: { eligible: !current || !ready, blocked: null, rank: null },
      parts: [
        {
          id: "receiver",
          label: words.receiver,
          owner: imam,
          fold: "never",
          draft: "none",
          state: { class: "waiting", word: words.thisAdministrator },
        markedByBox: true,
          render: () => (
            <p className="text-ui">
              {
                words.thisUsesYourAuthenticatedIMAMIdentityZoomAccountDataNeverChoosesThisPerson
              }
              {receiver ? <> {copy.receiverWillBeReplaced}</> : null}
            </p>
          ),
        },
      ],
      commit: {
        verb: receiver
          ? words.replaceReceiverWithThisAdministrator
          : words.bindThisAdministrator,
        cap: words.whenYouBindThisAdministrator,
        effects: () => [
          {
            id: "bind",
            sentence:
              words.recordingEventsUseYourCurrentIMAMAdministratorAuthority,
          },
        ],
        blockers: () => [],
        run: (key) =>
          outcome.run(
            key,
            async (id) => {
              const next = await zoomRecordingReceiverApi.bindCurrent(id);
              client.setQueryData(["admin", "zoom-recording-receiver"], next);
            },
            [words.zoomRecordingReceiverUpdated],
            async () => {
              await query.refetch();
            },
          ),
      },
    },
  ];
}
